import React from "react";
import { Link } from "react-router-dom";
import { Award, Warehouse, Cpu, TrendingUp } from "lucide-react";
import { Reveal, Eyebrow, PageHeader } from "../components/common";
import { AWARD_CATEGORIES } from "../mock";
import { useSiteData } from "../context/SiteData";

const CAT_ICONS = [Warehouse, Cpu, TrendingUp];

export default function Newspaper() {
  const { data, loaded } = useSiteData();

  // Real, admin-editable news/awards when any exist — the pinned (or first)
  // item becomes the featured banner, the rest become the category cards.
  const news = loaded && data.news.length ? [...data.news].sort((a, b) => (a.order || 0) - (b.order || 0)) : null;
  const featured = news ? (news.find((n) => n.pinned) || news[0]) : null;
  const rest = news ? news.filter((n) => n !== featured) : null;

  return (
    <>
      <PageHeader
        crumb="Newspaper"
        title="Recognition, not lifestyle marketing"
        intro="Fifteen years of awards for warehouse management, technology use and sales growth — the trust signal is operational, not promotional."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <Reveal>
          <div className="bg-gradient-to-br from-[#eef3fe] to-[#f7f9fd] border border-[var(--rs-border)] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row gap-8">
            <div className="shrink-0">
              <div className="w-16 h-16 rounded-xl bg-[var(--rs-blue)] text-white flex items-center justify-center"><Award size={30} /></div>
              {featured?.badge && <span className="font-mono-label text-2xl font-bold text-[var(--rs-blue)] block mt-3">{featured.badge}</span>}
              {!featured && <span className="font-mono-label text-2xl font-bold text-[var(--rs-blue)] block mt-3">'23</span>}
            </div>
            <div>
              <h2 className="font-head font-extrabold text-2xl md:text-3xl">{featured ? featured.title : "Hero MotoCorp — Best Distributor Award, 2022–23"}</h2>
              <p className="text-[var(--rs-muted)] mt-4 max-w-3xl leading-relaxed text-lg">
                {featured ? featured.body : "Presented to Director Sanjay Jain by Mr. Akhilesh Vijay, National Head Parts, during the National Parts Distributors Conference held in Malaysia — recognising Rajiv Syndicate's performance as Hero MotoCorp Super Stockiest across northern Haryana."}
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-[var(--rs-bg)] border-y border-[var(--rs-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <Reveal><Eyebrow>Fifteen years of recognition</Eyebrow></Reveal>
          <Reveal delay={80}><h2 className="font-head font-extrabold text-3xl md:text-5xl mt-4">Awarded across three categories</h2></Reveal>
          <Reveal delay={140}><p className="text-[var(--rs-muted)] text-lg mt-5 max-w-2xl">Consistent recognition across the categories that matter most for an authorised distributor's operations.</p></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {(rest && rest.length ? rest.map((n) => ({ title: n.title, body: n.body, category: n.category })) : AWARD_CATEGORIES).map((c, i) => {
              const Icon = CAT_ICONS[i % CAT_ICONS.length];
              return (
                <Reveal key={c.title} delay={i * 110}>
                  <div className="card-hover bg-white border border-[var(--rs-border)] rounded-2xl p-7 h-full">
                    <span className="font-mono-label text-xs text-[var(--rs-muted)] uppercase tracking-wider">{c.category || "Category"}</span>
                    <div className="w-12 h-12 rounded-xl bg-[#eef3fe] text-[var(--rs-blue)] flex items-center justify-center mt-4"><Icon size={22} /></div>
                    <h3 className="font-head font-bold text-xl mt-5">{c.title}</h3>
                    <p className="text-[var(--rs-muted)] text-sm mt-3 leading-relaxed">{c.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <Reveal>
          <div className="border border-dashed border-[var(--rs-border)] rounded-2xl p-8 md:p-10 text-center bg-white">
            <p className="text-[var(--rs-muted)] text-lg max-w-3xl mx-auto">
              Press clippings and certificates from these fifteen years of recognition are being digitised for this page. In the meantime, our team can share physical copies on request —{" "}
              <Link to="/contact" className="text-[var(--rs-blue)] font-semibold hover:underline">get in touch</Link>.
            </p>
          </div>
        </Reveal>
      </section>
    </>
  );
}
